import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiCall } from '../../config/api';
import DocumentEditor from '../../components/DocumentEditor';
import { FiX, FiSave, FiMaximize2 } from 'react-icons/fi';
import { DocumentTypeModel } from '../../types/document';

interface TemplateField {
	id: string;
	label: string;
	variable: string;
	category: string;
	description?: string;
}

const TEMPLATE_FIELDS: TemplateField[] = [
	// Date Fields
	{ id: 'date-short', label: 'Date (Short)', variable: '[LetterDateShort]', category: 'Date & Time', description: 'DD/MM/YYYY' },
	{ id: 'date-long', label: 'Date (Long)', variable: '[LetterDateLong]', category: 'Date & Time', description: 'DD Month YYYY' },
	{ id: 'current-time', label: 'Current Time', variable: '[CurrentTime]', category: 'Date & Time', description: 'HH:MM AM/PM' },
	
	// Provider Fields
	{ id: 'provider-salutation', label: 'Provider Salutation', variable: '[ProviderSalutation]', category: 'Provider', description: 'Dr, Mr, Ms, etc.' },
	{ id: 'provider-firstname', label: 'Provider First Name', variable: '[ProviderFirstName]', category: 'Provider' },
	{ id: 'provider-lastname', label: 'Provider Last Name', variable: '[ProviderLastName]', category: 'Provider' },
	{ id: 'provider-fullname', label: 'Provider Full Name', variable: '[ProviderFullName]', category: 'Provider' },
	{ id: 'provider-title', label: 'Provider Title', variable: '[ProviderTitle]', category: 'Provider', description: 'MBBS, FRACP, etc.' },
	{ id: 'provider-specialty', label: 'Provider Specialty', variable: '[ProviderSpecialty]', category: 'Provider' },
	{ id: 'provider-phone', label: 'Provider Phone', variable: '[ProviderPhone]', category: 'Provider' },
	{ id: 'provider-email', label: 'Provider Email', variable: '[ProviderEmail]', category: 'Provider' },
	
	// Recipient Fields
	{ id: 'recipient-salutation', label: 'Recipient Salutation', variable: '[RecipientSalutation]', category: 'Recipient', description: 'Dr, Mr, Ms, etc.' },
	{ id: 'recipient-firstname', label: 'Recipient First Name', variable: '[RecipientFirstName]', category: 'Recipient' },
	{ id: 'recipient-lastname', label: 'Recipient Last Name', variable: '[RecipientLastName]', category: 'Recipient' },
	{ id: 'recipient-fullname', label: 'Recipient Full Name', variable: '[RecipientFullName]', category: 'Recipient' },
	{ id: 'recipient-organization', label: 'Recipient Organization', variable: '[RecipientOrganization]', category: 'Recipient' },
	{ id: 'recipient-address', label: 'Recipient Address', variable: '[RecipientAddress]', category: 'Recipient', description: 'Full formatted address' },
	{ id: 'recipient-phone', label: 'Recipient Phone', variable: '[RecipientPhone]', category: 'Recipient' },
	{ id: 'recipient-mobile', label: 'Recipient Mobile', variable: '[RecipientMobile]', category: 'Recipient' },
	{ id: 'recipient-fax', label: 'Recipient Fax', variable: '[RecipientFax]', category: 'Recipient' },
	{ id: 'recipient-email', label: 'Recipient Email', variable: '[RecipientEmail]', category: 'Recipient' },
	{ id: 'recipient-specialty', label: 'Recipient Specialty', variable: '[RecipientSpecialty]', category: 'Recipient' },
	
	// Patient Fields
	{ id: 'patient-salutation', label: 'Patient Salutation', variable: '[PatientSalutation]', category: 'Patient', description: 'Mr, Ms, Mrs, etc.' },
	{ id: 'patient-firstname', label: 'Patient First Name', variable: '[PatientFirstName]', category: 'Patient' },
	{ id: 'patient-lastname', label: 'Patient Last Name', variable: '[PatientLastName]', category: 'Patient' },
	{ id: 'patient-fullname', label: 'Patient Full Name', variable: '[PatientFullName]', category: 'Patient' },
	{ id: 'patient-dob', label: 'Patient Date of Birth', variable: '[PatientDOB]', category: 'Patient' },
	{ id: 'patient-age', label: 'Patient Age', variable: '[PatientAge]', category: 'Patient' },
	{ id: 'patient-mrn', label: 'Patient MRN', variable: '[PatientMRN]', category: 'Patient', description: 'Medical Record Number' },
	{ id: 'patient-address', label: 'Patient Address', variable: '[PatientAddress]', category: 'Patient' },
	{ id: 'patient-phone', label: 'Patient Phone', variable: '[PatientPhone]', category: 'Patient' },
	{ id: 'patient-email', label: 'Patient Email', variable: '[PatientEmail]', category: 'Patient' },
	{ id: 'patient-medicare', label: 'Patient Medicare Number', variable: '[PatientMedicare]', category: 'Patient' },
	
	// Facility Fields
	{ id: 'facility-name', label: 'Facility Name', variable: '[FacilityName]', category: 'Facility' },
	{ id: 'facility-address', label: 'Facility Address', variable: '[FacilityAddress]', category: 'Facility' },
	{ id: 'facility-phone', label: 'Facility Phone', variable: '[FacilityPhone]', category: 'Facility' },
	{ id: 'facility-fax', label: 'Facility Fax', variable: '[FacilityFax]', category: 'Facility' },
	
	// Document Fields
	{ id: 'document-title', label: 'Document Title', variable: '[DocumentTitle]', category: 'Document' },
	{ id: 'document-type', label: 'Document Type', variable: '[DocumentType]', category: 'Document' },
	{ id: 'document-id', label: 'Document ID', variable: '[DocumentID]', category: 'Document' },
	{ id: 'letter-body', label: 'Letter Body', variable: '[LetterBody]', category: 'Document', description: 'AI-generated letter content' },
];

const TemplateEditor: React.FC = () => {
	const { token } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();
	const editorRef = useRef<any>(null);
	
	const [templateName, setTemplateName] = useState('');
	const [documentType, setDocumentType] = useState<string>('');
	const [documentTypes, setDocumentTypes] = useState<DocumentTypeModel[]>([]);
	const [description, setDescription] = useState('');
	const [content, setContent] = useState('');
	const [isActive, setIsActive] = useState(true);
	const [providerId, setProviderId] = useState<string>('');
	const [providers, setProviders] = useState<any[]>([]);
	const [saving, setSaving] = useState(false);
	const [selectedCategory, setSelectedCategory] = useState<string>('All');
	const [searchField, setSearchField] = useState('');
	const [shouldReloadEditor, setShouldReloadEditor] = useState(false);
	
	const editingTemplate = location.state?.template;
	
	useEffect(() => {
		fetchDocumentTypes();
		fetchProviders();
		
		// Check for backup from localStorage
		const backup = localStorage.getItem('template_backup');
		if (backup && !editingTemplate) {
			try {
				const backupData = JSON.parse(backup);
				const backupTime = new Date(backupData.timestamp);
				const hoursSinceBackup = (Date.now() - backupTime.getTime()) / (1000 * 60 * 60);
				
				// Only restore if backup is less than 24 hours old
				if (hoursSinceBackup < 24) {
					const restore = window.confirm(
						`Found a saved template from ${backupTime.toLocaleString()}. Would you like to restore it?`
					);
					
					if (restore) {
						setTemplateName(backupData.name || '');
						setDocumentType(backupData.document_type?.toString() || '');
						setDescription(backupData.description || '');
						setContent(backupData.content_template || '');
						setIsActive(backupData.is_active ?? true);
						setProviderId(backupData.provider_id || '');
						setShouldReloadEditor(true); // Trigger editor reload
						console.log('[TemplateEditor] Restored backup from localStorage');
					} else {
						localStorage.removeItem('template_backup');
					}
				} else {
					// Remove old backup
					localStorage.removeItem('template_backup');
				}
			} catch (error) {
				console.error('Error restoring backup:', error);
				localStorage.removeItem('template_backup');
			}
		}
		
		// If editing, populate fields
		if (editingTemplate) {
			setTemplateName(editingTemplate.name);
			setDocumentType(editingTemplate.document_type);
			setDescription(editingTemplate.description || '');
			setContent(editingTemplate.content_template || '');
			setIsActive(editingTemplate.is_active);
			setProviderId(editingTemplate.provider_id || '');
			// Trigger editor reload with template content
			if (editingTemplate.content_template) {
				setShouldReloadEditor(true);
			}
		}
	}, []);
	
	// Reload editor when content is restored from backup
	useEffect(() => {
		if (shouldReloadEditor && content && editorRef.current?.documentEditor) {
			try {
				console.log('[TemplateEditor] Reloading editor with restored content');
				editorRef.current.documentEditor.open(content);
				setShouldReloadEditor(false);
			} catch (error) {
				console.error('[TemplateEditor] Error loading restored content:', error);
			}
		}
	}, [shouldReloadEditor, content]);
	
	const fetchDocumentTypes = async () => {
		if (!token) return;
		
		try {
			const response = await apiCall('/documents/document-types/', {
				method: 'GET',
			}, token);

			if (response.ok) {
				const data = await response.json();
				const types = data.results || data || [];
				setDocumentTypes(types);
			}
		} catch (error) {
			console.error('Error fetching document types:', error);
		}
	};
	
	const fetchProviders = async () => {
		if (!token) return;
		
		try {
			const response = await apiCall('/providers/providers/', {
				method: 'GET',
			}, token);

			if (response.ok) {
				const data = await response.json();
				const providersList = data.results || data || [];
				console.log('[TemplateEditor] Fetched providers:', providersList.map((p: any) => ({ id: p.id, name: `${p.first_name} ${p.last_name}` })));
				setProviders(providersList);
			}
		} catch (error) {
			console.error('Error fetching providers:', error);
		}
	};
	
	const categories = ['All', ...Array.from(new Set(TEMPLATE_FIELDS.map(f => f.category)))];
	
	const filteredFields = TEMPLATE_FIELDS.filter(field => {
		const matchesCategory = selectedCategory === 'All' || field.category === selectedCategory;
		const matchesSearch = !searchField || 
			field.label.toLowerCase().includes(searchField.toLowerCase()) ||
			field.variable.toLowerCase().includes(searchField.toLowerCase());
		return matchesCategory && matchesSearch;
	});
	
	const handleFieldDragStart = (e: React.DragEvent, field: TemplateField) => {
		e.dataTransfer.setData('text/plain', field.variable);
		e.dataTransfer.effectAllowed = 'copy';
	};
	
	const handleSave = async () => {
		if (!templateName) {
			alert('Please enter a template name');
			return;
		}
		
		if (!token) return;
		
		setSaving(true);
		
		try {
			// Get the latest content from the editor
			let editorContent = content;
			if (editorRef.current?.documentEditor) {
				try {
					editorContent = editorRef.current.documentEditor.serialize();
					console.log('[TemplateEditor] Serialized content from editor');
				} catch (error) {
					console.warn('[TemplateEditor] Could not serialize editor content, using state:', error);
				}
			}
			
			console.log('[TemplateEditor] State before creating template data:', {
				documentType,
				providerId,
				templateName,
				description
			});
			
			const templateData = {
				name: templateName,
				document_type: documentType || null,
				description,
				content_template: editorContent,
				is_active: isActive,
				provider_id: providerId && providerId !== '' ? providerId : null,
			};
			
			console.log('[TemplateEditor] Template data to save:', {
				...templateData,
				provider_id_type: typeof templateData.provider_id,
				document_type_type: typeof templateData.document_type,
				content_preview: templateData.content_template?.substring(0, 100)
			});
			
			console.log('[TemplateEditor] Provider ID value:', templateData.provider_id);
			console.log('[TemplateEditor] Document Type value:', templateData.document_type);
			
			// Backup to localStorage before attempting save
			localStorage.setItem('template_backup', JSON.stringify({
				...templateData,
				timestamp: new Date().toISOString()
			}));
			
			console.log('[TemplateEditor] Saving template:', templateData);
			
			const url = editingTemplate 
				? `/correspondence/document-templates/${editingTemplate.id}/`
				: '/correspondence/document-templates/';
			
			const method = editingTemplate ? 'PUT' : 'POST';
			
			const response = await apiCall(url, {
				method,
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(templateData),
			}, token);
			
			console.log('[TemplateEditor] Response status:', response.status);
			
			if (response.ok) {
				const data = await response.json();
				console.log('[TemplateEditor] Save successful:', data);
				// Clear backup on success
				localStorage.removeItem('template_backup');
				navigate('/documents/templates');
			} else {
				// Try to get error details
				let errorData: any = {};
				const contentType = response.headers.get('content-type');
				
				try {
					if (contentType && contentType.includes('application/json')) {
						errorData = await response.json();
					} else {
						const textError = await response.text();
						errorData = { error: textError || `HTTP ${response.status}` };
					}
				} catch (parseError) {
					errorData = { error: `HTTP ${response.status} - Unable to parse response` };
				}
				
				console.error('[TemplateEditor] Save failed:', response.status, errorData);
				
				// Special handling for auth errors
				if (response.status === 401) {
					alert('Your session has expired. Your template has been saved locally. Please log in again and click Save Template to retry.');
				} else {
					alert(`Failed to save template (${response.status}): ${JSON.stringify(errorData, null, 2)}`);
				}
			}
		} catch (error) {
			console.error('[TemplateEditor] Error saving template:', error);
			alert(`Failed to save template: ${error}`);
		} finally {
			setSaving(false);
		}
	};
	
	const handleCancel = () => {
		if (window.confirm('Are you sure you want to cancel? Any unsaved changes will be lost.')) {
			navigate('/documents/templates');
		}
	};
	
	return (
		<div className="fixed inset-0 z-50 bg-base-100 flex flex-col">
			{/* Header */}
			<div className="bg-base-200 border-b border-base-300 px-6 py-4 flex items-center justify-between shrink-0">
				<div className="flex items-center gap-4 flex-1">
					<FiMaximize2 className="w-5 h-5 opacity-50" />
					<div className="flex-1 max-w-md">
						<input
							type="text"
							value={templateName}
							onChange={(e) => setTemplateName(e.target.value)}
							placeholder="Template Name"
							className="input input-bordered w-full"
						/>
					</div>
					<select
						value={documentType}
						onChange={(e) => setDocumentType(e.target.value)}
						className="select select-bordered w-64"
					>
						<option value="">Select Document Type</option>
						{documentTypes.map(type => (
							<option key={type.id} value={type.id.toString()}>
								{type.name}
							</option>
						))}
					</select>
					<select
						value={providerId}
						onChange={(e) => {
							const newValue = e.target.value;
							console.log('[TemplateEditor] Provider changed to:', newValue);
							setProviderId(newValue);
						}}
						className="select select-bordered w-64"
					>
						<option value="">All Providers (Shared)</option>
						{providers.map(provider => (
							<option key={provider.id} value={provider.id}>
								{provider.first_name} {provider.last_name}
							</option>
						))}
					</select>
					<label className="flex items-center gap-2 cursor-pointer">
						<input
							type="checkbox"
							checked={isActive}
							onChange={(e) => setIsActive(e.target.checked)}
							className="checkbox checkbox-primary checkbox-sm"
						/>
						<span className="text-sm">Active</span>
					</label>
				</div>
				<div className="flex items-center gap-2">
					<button
						onClick={handleSave}
						disabled={saving}
						className="btn btn-primary gap-2"
					>
						<FiSave className="w-4 h-4" />
						{saving ? 'Saving...' : 'Save Template'}
					</button>
					<button
						onClick={handleCancel}
						className="btn btn-ghost gap-2"
					>
						<FiX className="w-4 h-4" />
						Cancel
					</button>
				</div>
			</div>
			
			{/* Main Content Area */}
			<div className="flex-1 flex overflow-hidden min-h-0">
				{/* Editor Area */}
				<div className="flex-1 flex flex-col overflow-hidden min-w-0">
					{/* Description */}
					<div className="px-6 py-3 border-b border-base-300 shrink-0">
						<input
							type="text"
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							placeholder="Template Description (optional)"
							className="input input-bordered input-sm w-full"
						/>
					</div>
					
					{/* Document Editor */}
					<div className="flex-1 min-h-0">
						<DocumentEditor
							ref={editorRef}
							initialContent={content}
							onChange={setContent}
							isReadOnly={false}
							height="100%"
						/>
					</div>
				</div>
				
				{/* Fields Sidebar */}
				<div className="w-80 border-l border-base-300 flex flex-col bg-base-200">
					<div className="p-4 border-b border-base-300">
						<h3 className="font-semibold mb-3">Template Fields</h3>
						
						{/* Search */}
						<input
							type="text"
							value={searchField}
							onChange={(e) => setSearchField(e.target.value)}
							placeholder="Search fields..."
							className="input input-bordered input-sm w-full mb-3"
						/>
						
						{/* Category Tabs */}
						<div className="flex flex-wrap gap-1">
							{categories.map(cat => (
								<button
									key={cat}
									onClick={() => setSelectedCategory(cat)}
									className={`btn btn-xs ${
										selectedCategory === cat 
											? 'btn-primary' 
											: 'btn-ghost'
									}`}
								>
									{cat}
								</button>
							))}
						</div>
					</div>
					
					{/* Fields List */}
					<div className="flex-1 overflow-y-auto p-4 space-y-2">
						{filteredFields.map(field => (
							<div
								key={field.id}
								draggable
								onDragStart={(e) => handleFieldDragStart(e, field)}
								className="bg-base-100 p-3 rounded-lg border border-base-300 cursor-move hover:border-primary hover:shadow-md transition-all"
								title={field.description}
							>
								<div className="font-medium text-sm mb-1">{field.label}</div>
								<div className="text-xs font-mono opacity-60 bg-base-200 px-2 py-1 rounded">
									{field.variable}
								</div>
								{field.description && (
									<div className="text-xs opacity-50 mt-1">{field.description}</div>
								)}
							</div>
						))}
						
						{filteredFields.length === 0 && (
							<div className="text-center text-sm opacity-50 py-8">
								No fields found
							</div>
						)}
					</div>
					
					{/* Instructions */}
					<div className="p-4 border-t border-base-300 bg-base-100">
						<div className="text-xs opacity-70">
							<p className="font-semibold mb-1">💡 How to use:</p>
							<p>Drag and drop fields into the editor to add them to your template.</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default TemplateEditor;
